import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  changeSelectedQuantity,
  setSelectedSKU,
  setSelectedFirstProductVariant,
  setSelectedSecondSKU,
} from '../../../slices/pdp';
import {
  getVariantsWithPercentDiscount,
  findVariantByAttributeValues,
} from '../../../utils/variants';
import ProductPartSelection from '../ProductPartSelection';
import ProductVariantSelection from '../ProductVariantSelection';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import {
  priceToString,
  getTargetProductWithAllVariantsBySKU,
} from '../../../utils';

import {
  SIZE_ATTRIBUTES,
  COLOR_ATTRIBUTES,
  HEIGHT_ATTRIBUTES,
  GENDER_ATTRIBUTES,
  QUANTITY_ATTRIBUTES,
  FEEL_ATTRIBUTES,
  DEPTH_ATTRIBUTES,
} from '../constants';
import useQueryParams from '../../../hooks/useQueryParams';
import useGetActiveCart from '../../../hooks/queries/useGetActiveCart/useGetActiveCart';
import { productTypeToSingularProductNameConverter } from '../../../utils/product';

const ProductSelection = () => {
  const {
    aovs,
    product: productFromDrupalSettings,
    bedframeSelector,
  } = useDrupalSettings();
  const { publish } = drupalPubSubService;

  const [selectedProduct, setSelectedProduct] = useState(
    productFromDrupalSettings,
  );

  const [productDiscountSavings, setProductDiscountSavings] =
    useState(undefined);

  const { formatMessage, formatNumber } = useIntl();

  const [
    productVariantsWithDiscountedPrices,
    setProductVariantsWithDiscountedPrices,
  ] = useState(selectedProduct?.variants ?? []);

  const productType = selectedProduct?.productType;

  const dispatch = useDispatch();

  const {
    selectedSKU,
    selectedFirstProductVariant,
    selectedQty,
    selectedSecondSKU,
  } = useSelector((state) => state.pdp);

  const hasAnItemSelected = useSelector(
    (state) => state.pdp.selectedBundlePdpAOVSKUs.length > 0,
  );

  useGetActiveCart();

  const anonymousId = useSelector((state) => state.cart?.anonymousId);
  localStorage.setItem('anonymousId', anonymousId);

  const [userSelectedVariant, setUserSelectedVariant] = useState(
    selectedFirstProductVariant,
  );
  const [
    productSize,
    productHeight,
    productColor,
    gender,
    productQuantity,
    productFeel,
    productDepth,
  ] = useQueryParams([
    'size',
    'height',
    'color',
    'gender',
    'quantity',
    'feel',
    'depth',
  ]);

  const productFromDrupalSettingsAsJSON = productFromDrupalSettings
    ? JSON.stringify(productFromDrupalSettings)
    : undefined;

  useEffect(() => {
    if (!hasAnItemSelected) {
      const productFromDrupalSettingsValue = productFromDrupalSettingsAsJSON
        ? JSON.parse(productFromDrupalSettingsAsJSON)
        : {};
      setSelectedProduct(productFromDrupalSettingsValue);
    }
  }, [hasAnItemSelected, productFromDrupalSettingsAsJSON]);

  useEffect(() => {
    const discountPercentage = selectedProduct?.secondItemDiscount?.percentage;

    const newProductVariantsWithDiscounts = selectedProduct?.variants.map(
      (variant) =>
        selectedSecondSKU && discountPercentage
          ? getVariantsWithPercentDiscount([variant], discountPercentage)?.[0]
          : variant,
    );
    setProductVariantsWithDiscountedPrices(newProductVariantsWithDiscounts);
  }, [
    selectedSecondSKU,
    selectedProduct?.secondItemDiscount,
    selectedProduct?.variants,
  ]);

  const updateDiscountSavings = useCallback(
    (discount) => {
      if (discount.centAmount > 0) {
        setProductDiscountSavings(
          priceToString({
            price: discount,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          }),
        );
      } else {
        setProductDiscountSavings(undefined);
      }
    },
    [formatNumber, formatMessage],
  );

  useEffect(() => {
    if (!selectedSKU) {
      const masterVariant = productVariantsWithDiscountedPrices.find(
        (variant) => variant.masterVariant === true,
      );

      const variant = masterVariant || productVariantsWithDiscountedPrices[0];
      dispatch(setSelectedFirstProductVariant({ variant }));

      updateDiscountSavings(variant.discount);

      dispatch(setSelectedSKU({ selectedSKU: variant.sku, selectedQty: 1 }));
      setUserSelectedVariant(variant);
    }
  }, [
    dispatch,
    productVariantsWithDiscountedPrices,
    selectedSKU,
    formatNumber,
    formatMessage,
    updateDiscountSavings,
  ]);

  const quantityIncrementHandler = useCallback(
    () => dispatch(changeSelectedQuantity({ selectedQty: selectedQty + 1 })),
    [dispatch, selectedQty],
  );
  const quantityDecrementHandler = useCallback(
    () => dispatch(changeSelectedQuantity({ selectedQty: selectedQty - 1 })),
    [dispatch, selectedQty],
  );

  const selectFirstSKUHandler = useCallback(
    (sku, isUserSelected = true) => {
      const newSelectedFirstProductVariant = sku
        ? productFromDrupalSettings.variants.find(
            (variant) => variant.sku === sku,
          ) ||
          (bedframeSelector &&
            bedframeSelector.productParts &&
            bedframeSelector.productParts
              .flatMap((partProduct) => partProduct.variants)
              .find((variant) => variant.sku === sku))
        : undefined;

      if (newSelectedFirstProductVariant) {
        dispatch(
          setSelectedFirstProductVariant({
            variant: newSelectedFirstProductVariant,
          }),
        );

        updateDiscountSavings(newSelectedFirstProductVariant.discount);
        if (isUserSelected) {
          setUserSelectedVariant(newSelectedFirstProductVariant);
        }
      }

      dispatch(setSelectedSKU({ selectedSKU: sku }));
      publish({
        subscription: 'onVariantChange',
        parameters: newSelectedFirstProductVariant,
      });
    },
    [
      bedframeSelector,
      dispatch,
      productFromDrupalSettings.variants,
      publish,
      updateDiscountSavings,
    ],
  );

  const filterProductBasedOnQueryParams = (
    productVariant,
    parameter,
    filterBy,
  ) => ({
    ...productVariant,
    attributes:
      productVariant.attributes.filter(
        (attribute) =>
          filterBy.includes(attribute.name) &&
          (attribute.key.toLowerCase() === parameter.toLowerCase() ||
            attribute.label.toLowerCase().replace(/ +/i, '_') ===
              parameter.toLowerCase() ||
            attribute.key
              .toLowerCase()
              .split('/')
              .includes(parameter.toLowerCase()) ||
            (attribute.key.toLowerCase().match(/\d+/) &&
              attribute.key.toLowerCase().match(/\d+/)[0] ===
                parameter.toLowerCase())),
      ).length > 0 && productVariant.attributes,
  });

  useEffect(() => {
    let selectedVariant = null;
    const { variants } = productFromDrupalSettings;
    if (productSize) {
      selectedVariant = variants
        .map((element) =>
          filterProductBasedOnQueryParams(
            element,
            productSize,
            SIZE_ATTRIBUTES,
          ),
        )
        .filter((variant) => variant.attributes.length > 0);
      if (productHeight) {
        selectedVariant = selectedVariant
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productHeight,
              HEIGHT_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
      }
      if (productColor) {
        selectedVariant = selectedVariant
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productColor,
              COLOR_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
      }
      if (gender) {
        selectedVariant = selectedVariant
          .map((element) =>
            filterProductBasedOnQueryParams(element, gender, GENDER_ATTRIBUTES),
          )
          .filter((variant) => variant.attributes.length > 0);
      }
      if (productDepth) {
        selectedVariant = selectedVariant
          .map((element) =>
            filterProductBasedOnQueryParams(
              element,
              productDepth,
              DEPTH_ATTRIBUTES,
            ),
          )
          .filter((variant) => variant.attributes.length > 0);
      }
    } else if (productColor) {
      selectedVariant = variants
        .map((element) =>
          filterProductBasedOnQueryParams(
            element,
            productColor,
            COLOR_ATTRIBUTES,
          ),
        )
        .filter((variant) => variant.attributes.length > 0);
    } else if (productQuantity) {
      selectedVariant = variants
        .map((element) =>
          filterProductBasedOnQueryParams(
            element,
            productQuantity,
            QUANTITY_ATTRIBUTES,
          ),
        )
        .filter((variant) => variant.attributes.length > 0);
    } else if (productFeel) {
      selectedVariant = variants
        .map((element) =>
          filterProductBasedOnQueryParams(
            element,
            productFeel,
            FEEL_ATTRIBUTES,
          ),
        )
        .filter((variant) => variant.attributes.length > 0);
    }
    if (selectedVariant) {
      const sku = selectedVariant[0]?.sku;
      selectFirstSKUHandler(sku, false);
    }
    // Disabled eslint-disable-next-line, react-hooks/exhaustive-deps
    // since it is required to run only once which is when the component is mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFromDrupalSettingsAsJSON]);

  const selectSecondSKUHandler = (sku) => {
    dispatch(setSelectedSecondSKU(sku));
  };

  const changeSelectedProductPartHandler = (productId) => {
    if (selectedProduct?.id !== productId) {
      const newSelectedProduct =
        bedframeSelector?.productParts?.find(
          (partProduct) => partProduct.id === productId,
        ) || productFromDrupalSettings;

      const currentSelectedVariant = selectedProduct.variants.find(
        (variant) => variant.sku === selectedSKU,
      );

      const newSelectedVariant =
        findVariantByAttributeValues({
          variants: newSelectedProduct.variants,
          attributeValues:
            userSelectedVariant?.attributes ||
            currentSelectedVariant.attributes,
        }) || newSelectedProduct.variants[0];

      selectFirstSKUHandler(newSelectedVariant.sku, false);
      selectSecondSKUHandler(undefined);
      setSelectedProduct(newSelectedProduct);
    }
  };

  useEffect(() => {
    if (hasAnItemSelected) {
      const bundleAOVsV1 = aovs?.find(
        ({ targetSku, bundles }) =>
          targetSku === selectedSKU && bundles?.showOnPdp,
      );
      if (bundleAOVsV1) {
        const { bundles } = bundleAOVsV1;
        const { items } = bundles;

        const bundleTargetProduct = getTargetProductWithAllVariantsBySKU(
          items,
          selectedSKU,
        );
        setSelectedProduct(bundleTargetProduct);
      } else {
        const bundleAOVsV2 = aovs?.find(
          ({ targetSku, bundles }) =>
            targetSku === selectedSKU && bundles?.showOnPdpV2,
        );
        const bundleTargetProduct = getTargetProductWithAllVariantsBySKU(
          bundleAOVsV2?.bundles?.items,
          selectedSKU,
        );
        setSelectedProduct(bundleTargetProduct);
      }
    }
  }, [aovs, hasAnItemSelected, selectedSKU]);

  return (
    <>
      <ProductVariantSelection
        product={{
          name: selectedProduct?.name,
          key: selectedProduct?.key,
          secondItemDiscount: {
            percentage: selectedProduct?.secondItemDiscount?.percentage,
            preAddedMessage:
              selectedProduct?.secondItemDiscount?.preAddedMessage,
            postAddedMessage:
              selectedProduct?.secondItemDiscount?.postAddedMessage,
          },
          variants: productVariantsWithDiscountedPrices,
          productTypeSingular: productTypeToSingularProductNameConverter(
            productType,
            selectedProduct?.product_category,
          ),
          attributesExtraInfo: selectedProduct?.attribute_links,
        }}
        selectedFirstSKU={selectedSKU}
        onSelectFirstSKU={selectFirstSKUHandler}
        selectedFirstSKUQuantity={selectedQty}
        selectedSecondSKU={selectedSecondSKU}
        onSelectSecondSKU={selectSecondSKUHandler}
        onFirstProductQuantityIncrement={quantityIncrementHandler}
        onFirstProductQuantityDecrement={quantityDecrementHandler}
        selectedFirstSKUDiscount={productDiscountSavings}
        hasProductParts={bedframeSelector?.productParts?.length > 0}
      />
      {bedframeSelector && (
        <ProductPartSelection
          bedframeSelector={bedframeSelector}
          selectedProductVariant={selectedFirstProductVariant}
          onChangeSelectedProductPart={changeSelectedProductPartHandler}
        />
      )}
    </>
  );
};

export default ProductSelection;
